import React, { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, Phone } from "lucide-react"

const navLinks = [
  { label: "HOME", href: "/" },
  { label: "WORKS", href: "/works" },
  { label: "SERVICES", href: "/services" },
  { label: "JOURNAL", href: "/journal" },
  { label: "ABOUT", href: "/about" },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location])

  const isHome = location.pathname === "/"

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || !isHome
            ? "bg-stone-950/95 backdrop-blur-md border-b border-stone-800/50 py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/mainlogo.png"
              alt="Bastu Binyash"
              className="h-10 w-auto object-contain"
            />
            <div className="leading-none">
              <span className="block font-['Cormorant_Garamond'] text-xl font-semibold tracking-[0.2em] text-stone-100 group-hover:text-amber-300 transition-colors uppercase">
                Bastu Binyash
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-['Space_Grotesk'] text-[11px] tracking-[0.25em] font-medium transition-colors relative group ${
                  location.pathname === link.href
                    ? "text-amber-400"
                    : "text-stone-400 hover:text-stone-100"
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-amber-400 transition-all duration-300 ${
                    location.pathname === link.href ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+8801711934078"
              className="flex items-center gap-2 font-['Space_Grotesk'] text-[11px] tracking-[0.2em] font-medium bg-amber-400 text-stone-950 px-5 py-2.5 hover:bg-amber-300 transition-all duration-300 group"
            >
              <Phone size={13} className="group-hover:animate-pulse" />
              CALL NOW
            </a>
            <Link
              to="/contact"
              className="font-['Space_Grotesk'] text-[11px] tracking-[0.2em] font-medium border border-amber-400 text-amber-400 px-5 py-2.5 hover:bg-amber-400 hover:text-stone-950 transition-all duration-300"
            >
              START A PROJECT
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-stone-300 hover:text-stone-100 transition-colors p-1"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-stone-950/98 backdrop-blur-lg flex flex-col justify-center items-center transition-all duration-500 ${
          menuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <nav className="flex flex-col items-center gap-8">
          {navLinks.map((link, i) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-['Cormorant_Garamond'] text-4xl font-light tracking-widest transition-colors ${
                location.pathname === link.href ? "text-amber-400" : "text-stone-200 hover:text-amber-300"
              }`}
              style={{ transitionDelay: menuOpen ? `${i * 60}ms` : "0ms" }}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="tel:+8801711934078"
            className="mt-2 flex items-center justify-center gap-2 font-['Space_Grotesk'] text-sm tracking-[0.2em] bg-amber-400 text-stone-950 px-8 py-3 hover:bg-amber-300 transition-all duration-300"
          >
            <Phone size={14} />
            CALL NOW
          </a>
          <Link
            to="/contact"
            className="mt-2 font-['Space_Grotesk'] text-sm tracking-[0.2em] border border-amber-400 text-amber-400 px-8 py-3 hover:bg-amber-400 hover:text-stone-950 transition-all duration-300"
          >
            START A PROJECT
          </Link>
        </nav>
      </div>
    </>
  )
}
